import { convertToPluralIfNeeded, createAppRoutes, getInitialsFromName, useViewport } from '@bpm-web-app/utils';
import { useCreateDrive, useGetDrives, useGetMeSubscription, useGetSharedDrives } from '@bpm-web-app/swr-hooks';
import { Fragment, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Drive } from '@bpm-web-app/create-api-sdk';
import styles from './my-drive.module.css';
import CardCreate from '../../shared/card/card-create';
import { LocalSearchSort } from '../../nav-my-playlist-categories/nav-my-playlist-categories';
import PlaylistsForm from '../../playlists-form/playlists-form';
import { PageHeader } from '../../shared/page-header/page-header';
import { EmptyState, GhostComponent } from '../../shared';

/* eslint-disable-next-line @typescript-eslint/no-empty-interface */
export interface MyDriveProps { }

export function MyDrive(props: MyDriveProps) {
    const { data: driveData, isLoading } = useGetDrives();
    const { data: sharedDrivesData } = useGetSharedDrives(true);

    const { data: createSubscriptionData } = useGetMeSubscription('create', false);
    const { isMobile } = useViewport();
    const [searchTerm, setSearchTerm] = useState('');
    const [sortType, setSortType] = useState<LocalSearchSort>(undefined);
    const [showCreateForm, setShowCreateForm] = useState<boolean>(false);

    const createDriveFolder = useCreateDrive();

    const CreateDriveForm = useMemo(
        () => (
            <PlaylistsForm
                type="CreateNewDrive"
                text="Create a personal collection of sounds to help organize your projects."
                formAction={(name) => createDriveFolder({ name })}
                close={() => setShowCreateForm(false)}
            />
        ),
        [createDriveFolder]
    );

    const getDescriptionMarkup = (soundCount: number) => <div className={styles['my-drive__tracks']}>{`${convertToPluralIfNeeded(soundCount, 'Sound')}`}</div>;

    const drivesList = useMemo(() => {
        let tempArray: Drive[] = [];
        switch (sortType) {
            case 'most_recent':
                tempArray = driveData?.slice().sort((a, b) => (a.created_at < b.created_at ? 1 : -1)) || [];
                break;
            case 'title_asc':
                tempArray = driveData?.slice().sort((a, b) => (a.name > b.name ? 1 : -1)) || [];
                break;
            case 'title_desc':
                tempArray = driveData?.slice().sort((a, b) => (a.name < b.name ? 1 : -1)) || [];
                break;
            default:
                tempArray = driveData?.slice() || [];
                break;
        }

        if (searchTerm.trim()) {
            tempArray = tempArray.filter(({ name }) => name.toLowerCase().includes(searchTerm.toLowerCase()));
        }

        return tempArray;
    }, [driveData, searchTerm, sortType]);

    const hasDrives = useMemo(() => drivesList?.length !== 0, [drivesList?.length]);

    const isSubscribedToAPlan = useMemo(() => {
        if (createSubscriptionData?.data?.membership?.has_membership) {
            return true;
        }
        return false;
    }, [createSubscriptionData?.data?.membership?.has_membership]);

    const renderEmptyState = useMemo(() => {
        if (isSubscribedToAPlan) {
            return (
                <EmptyState title="Looks like you haven't created any drives yet." subtitle="Start building your own personalized drives by adding your favorite sounds. Your drives will appear here once you've created them." actionLabel="Create Drive" onPress={() => setShowCreateForm(true)} noPadding verticalMargins={16} />
            );
        }
        return (
            <EmptyState title="Subscribe today to start building your library." subtitle="Start building your own personalized drives by adding your favorite sounds. Your drives will appear here once you've created them." actionLabel="Get Started" href="account/plan" icon="heart-outline-icon" actionIcon="chevron-right-icon" noPadding verticalMargins={16} />
        );
    }, [isSubscribedToAPlan]);

    return (
        <div className={classNames(styles['my-drive'], 'spacing__window')}>
            {hasDrives ?
                (<PageHeader
                    title={drivesList ? `My Drives (${drivesList.length})` : 'My Drives'}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    searchPlaceholder="Search Drives"
                    handleAdd={() => setShowCreateForm(true)}
                    onSort={setSortType}
                    selectedSortType={sortType}
                />
                ) : (
                    <PageHeader
                        title={drivesList ? `My Drives (${drivesList.length})` : 'My Drives'}
                    />
                )}
            {showCreateForm && CreateDriveForm}
            {!hasDrives && renderEmptyState}
            {isLoading ? (
                <GhostComponent type="cards" elementsCount={16} cardSize="user-playlist" linesWidthArray={[90, 70]} grid noPadding />
            ) : (
                <div className={styles['my-drive__grid']}>
                    {sharedDrivesData && sharedDrivesData.data && sharedDrivesData?.data?.length > 0 ? (
                        <Fragment key="shared-playlist-drive">
                            <CardCreate
                                contentType="drive"
                                title="Shared With Me"
                                description=""
                                id="shared-playlist-drive"
                                cardSize="user-playlist"
                                link={createAppRoutes.myDriveId('shared')}
                                imageUrl=""
                                hideMoreOptions
                            />
                        </Fragment>
                    ) : null}
                    {drivesList.map((item) => (
                        <Fragment key={item.id}>
                            <CardCreate
                                contentType="drive"
                                imageUrl={item.custom_image_url || item.image_url}
                                title={item.name}
                                description={isMobile ? undefined : getDescriptionMarkup(item.sound_count)}
                                id={item.id}
                                cardSize="user-playlist"
                                userPlaylistConfig={{
                                    initials: getInitialsFromName(item.name),
                                    imageBgColor: item?.image_color || '#FFFFFF',
                                }}
                                link={createAppRoutes.myDriveId(item.id)}
                            />
                        </Fragment>
                    ))}
                </div>)}
        </div>
    );
}

export default MyDrive;
